import { useEffect } from 'react';

import { EXPANDED_COLLAPSE_PX, EXPAND_PX, PEEK_DISMISS_PX } from './constants';
import type { UseSheetDragParams } from './types';

const DRAG_OFFSET_VAR = '--airspace-sheet-drag-offset';
const DRAGGING_ATTRIBUTE = 'data-dragging';

// Mobile-only peek <-> expanded drag. Hook binds native pointer-capture
// handlers to the SheetHeader drag handle (via ref) and writes a live
// translateY offset via a CSS custom property on the sheet element so the
// transition timing can switch off during the drag (see styles.module.scss:
// `[data-dragging='true']`).
const useSheetDrag = ({
    dragHandleRef,
    isEnabled,
    isExpanded,
    onCollapse,
    onDismiss,
    onExpand,
    sheetRef
}: UseSheetDragParams): void => {
    useEffect(() => {
        const handle = dragHandleRef.current;

        if (!isEnabled || handle === null) {
            return undefined;
        }

        let startY: number | null = null;
        let currentDy = 0;

        const writeOffset = (offsetPx: number) => {
            const sheet = sheetRef.current;

            if (sheet === null) {
                return;
            }

            sheet.style.setProperty(DRAG_OFFSET_VAR, `${offsetPx}px`);
        };

        const clearDragState = () => {
            const sheet = sheetRef.current;

            if (sheet !== null) {
                sheet.removeAttribute(DRAGGING_ATTRIBUTE);
                sheet.style.removeProperty(DRAG_OFFSET_VAR);
            }

            startY = null;
            currentDy = 0;
        };

        const resolveDragEnd = (dy: number) => {
            if (dy < -EXPAND_PX) {
                onExpand();

                return;
            }

            if (dy > PEEK_DISMISS_PX && !isExpanded) {
                onDismiss();

                return;
            }

            if (dy > EXPANDED_COLLAPSE_PX && isExpanded) {
                onCollapse();
            }
        };

        const handlePointerDown = (event: PointerEvent) => {
            startY = event.clientY;
            currentDy = 0;

            handle.setPointerCapture(event.pointerId);

            const sheet = sheetRef.current;

            if (sheet !== null) {
                sheet.setAttribute(DRAGGING_ATTRIBUTE, 'true');
            }
        };

        const handlePointerMove = (event: PointerEvent) => {
            if (startY === null) {
                return;
            }

            // Allow upward drag to float the sheet but resist downward drag
            // so the sheet visually stays anchored to the bottom edge.
            const dy = event.clientY - startY;
            const visualOffset = dy > 0 ? dy * 0.5 : dy;

            currentDy = dy;

            writeOffset(visualOffset);
        };

        const handlePointerUp = (event: PointerEvent) => {
            if (startY === null) {
                return;
            }

            const dy = currentDy;

            if (handle.hasPointerCapture(event.pointerId)) {
                handle.releasePointerCapture(event.pointerId);
            }

            clearDragState();
            resolveDragEnd(dy);
        };

        const handlePointerCancel = (event: PointerEvent) => {
            if (handle.hasPointerCapture(event.pointerId)) {
                handle.releasePointerCapture(event.pointerId);
            }

            clearDragState();
        };

        handle.addEventListener('pointerdown', handlePointerDown);
        handle.addEventListener('pointermove', handlePointerMove);
        handle.addEventListener('pointerup', handlePointerUp);
        handle.addEventListener('pointercancel', handlePointerCancel);

        return () => {
            handle.removeEventListener('pointerdown', handlePointerDown);
            handle.removeEventListener('pointermove', handlePointerMove);
            handle.removeEventListener('pointerup', handlePointerUp);
            handle.removeEventListener('pointercancel', handlePointerCancel);
            clearDragState();
        };
    }, [dragHandleRef, isEnabled, isExpanded, onCollapse, onDismiss, onExpand, sheetRef]);
};

export default useSheetDrag;
